import type { FC } from 'react';
import React, { useState } from 'react';
import { writeTextInClipboard } from 'src/utils/clipboard';
import { Icon } from '../icon';
import { Input } from '../input';
import { Button } from '../button';
import { message } from 'src/common/components/message';
import { isUnsafeUrl } from '@flowus/common/url';
import { Tooltip } from '@flowus/common/tooltip';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import { cx } from '@flowus/common/cx';

export enum LinkWidgetType {
  external = 0,
  internal = 1,
}

export interface LinkWidgetProp {
  linkType?: LinkWidgetType;
  onLinkChange?: (value: string) => void;
  onSetLink?: (linkUrl?: string) => void;
  onClearLink?: (isFocus?: boolean) => void;
  onCloseModal: () => void;
  inLink?: boolean;
  link: string;
  isInternalLink?: boolean;
  linkTitle?: string;
  onLinkTitleChange?: (value: string) => void;
  onCancel?: () => void;
}

interface UniversalLinkProps {
  isInternalLink: boolean;
}

const ExternalLinkPanel: FC<Omit<LinkWidgetProp, 'linkType'> & UniversalLinkProps> = ({
  inLink,
  link,
  onLinkChange,
  onSetLink,
  onCloseModal,
  isInternalLink,
  onClearLink,
  linkTitle,
  onLinkTitleChange,
  onCancel,
}) => {
  const { t } = useAppTranslation();
  const [editable, setEditable] = useState(isInternalLink);
  const hasTitleSupport = !!onLinkTitleChange;

  const handleConfirm = () => {
    if (isUnsafeUrl(link)) {
      message.error(t('components.image.external_link_invalid'));
      return;
    }
    onSetLink?.(isInternalLink ? '' : link);
    onCloseModal();
  };

  const handleCancel = () => {
    onCancel?.();
    onCloseModal();
  };

  // 带标题编辑的新版编辑表单
  if (hasTitleSupport) {
    // 查看模式：显示链接预览和操作按钮
    if (inLink && !isInternalLink && !editable) {
      return (
        <div className="flex items-center w-full justify-between space-x-1">
          <div className="flex items-center space-x-1 min-w-[140px]">
            <Icon name={'IcGlobeShare'} size="middle" />
            <span className="text-ellipsis">{link}</span>
          </div>
          <div className="shrink-0 flex items-center space-x-1">
            <Tooltip
              className="p-1 animate-hover"
              popup={t('common.components.link_widget.edit_link')}
              onClick={() => {
                setEditable(true);
              }}
            >
              <Icon name={'IcBtnEdit'} size="small" />
            </Tooltip>
            <Tooltip
              className="p-1 animate-hover"
              popup={t('common.components.link_widget.copy_link')}
              onClick={() => {
                void writeTextInClipboard(link, {
                  message: t('common.components.link_widget.link_copied'),
                });
              }}
            >
              <Icon name={'IcDuplicate'} size="small" />
            </Tooltip>
            <Tooltip
              className="p-1 animate-hover"
              popup={t('common.components.link_widget.clear_link')}
              onClick={() => {
                onClearLink?.();
                onCloseModal();
              }}
            >
              <Icon name={'IcTrash'} size="small" />
            </Tooltip>
          </div>
        </div>
      );
    }

    // 编辑模式：URL + 标题输入框 + 取消/确定按钮
    return (
      <div className="flex flex-col gap-4 w-full">
        <div className="flex flex-col gap-2">
          <span className="text-t2-medium text-grey2">
            {t('common.components.link_widget.page_or_url')}
          </span>
          <Input
            autoFocus
            placeholder={t('common.components.link_widget.enter_webpage_link')}
            value={isInternalLink ? '' : link}
            className="w-full h-8"
            onEnter={handleConfirm}
            onChange={(value) => {
              onLinkChange?.(value);
            }}
          />
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-t2-medium text-grey2">
            {t('common.components.link_widget.link_title')}
          </span>
          <Input
            placeholder={t('common.components.link_widget.link_title')}
            value={linkTitle ?? ''}
            className="w-full h-8"
            onEnter={handleConfirm}
            onChange={(value) => {
              onLinkTitleChange?.(value);
            }}
          />
        </div>
        <div className="border-t border-grey5 pt-3 flex items-center justify-end gap-2.5">
          <button
            className="text-t2-medium text-grey1 px-3 py-1.5 rounded animate-hover"
            onClick={handleCancel}
          >
            {t('common.cancel')}
          </button>
          <Button colorType="active" size="large" onClick={handleConfirm}>
            {t('common.components.link_widget.confirm')}
          </Button>
        </div>
      </div>
    );
  }

  // 旧版 UI（用于图片链接等不需要标题编辑的场景）
  return (
    <>
      {inLink && !isInternalLink && !editable ? (
        <>
          <div className="flex items-center w-full justify-between space-x-1">
            <div className="flex items-center space-x-1 min-w-[140px]">
              <Icon name={'IcGlobeShare'} size="middle" />
              <span className="text-ellipsis">{link}</span>
            </div>
            <div className="shrink-0 flex items-center space-x-1">
              <Tooltip
                className="p-1 animate-hover"
                popup={t('common.components.link_widget.edit_link')}
                onClick={() => {
                  setEditable(true);
                }}
              >
                <Icon name={'IcBtnEdit'} size="small" />
              </Tooltip>

              <Tooltip
                className="p-1 animate-hover"
                popup={t('common.components.link_widget.copy_link')}
                onClick={() => {
                  void writeTextInClipboard(link, {
                    message: t('common.components.link_widget.link_copied'),
                  });
                }}
              >
                <Icon name={'IcDuplicate'} size="small" />
              </Tooltip>
              <Tooltip
                className="p-1 animate-hover"
                popup={t('common.components.link_widget.clear_link')}
                onClick={() => {
                  onClearLink?.();
                  onCloseModal();
                }}
              >
                <Icon name={'IcTrash'} size="small" />
              </Tooltip>
            </div>
          </div>
        </>
      ) : (
        <div className="w-full flex items-center justify-between">
          <Input
            autoFocus
            placeholder={t('common.components.link_widget.enter_webpage_link')}
            value={isInternalLink ? '' : link}
            className="w-full mx-2.5 my-2.5 h-8"
            onEnter={() => {
              if (isUnsafeUrl(link)) {
                message.error(t('components.image.external_link_invalid'));
                return;
              }
              onSetLink?.(isInternalLink ? '' : link);
              onCloseModal();
            }}
            onChange={(value) => {
              onLinkChange?.(value);
            }}
          />
          <Button
            colorType="active"
            size="large"
            onClick={() => {
              if (isUnsafeUrl(link)) {
                message.error(t('components.image.external_link_invalid'));
                return;
              }
              onSetLink?.(isInternalLink ? '' : link);
              onCloseModal();
            }}
          >
            {t('common.components.link_widget.confirm')}
          </Button>
        </div>
      )}
    </>
  );
};

export const LinkWidget: FC<LinkWidgetProp> = ({
  // linkType = LinkWidgetType.external,
  isInternalLink = false,
  ...props
}) => {
  const hasTitleSupport = !!props.onLinkTitleChange;
  return (
    <div
      className={cx(
        'next-modal flex text-t2',
        hasTitleSupport ? 'w-[413px] p-4' : 'w-[360px] p-1 items-center text-grey3'
      )}
    >
      <ExternalLinkPanel {...props} isInternalLink={isInternalLink} />
    </div>
  );
};
